import React, { RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import _ from 'underscore';
import { ModuleStateDB } from '../../../../data/datatypes';
import { getVisibleSessionIdsForUser, getVisibleSections, moduleIsVisibleForUser } from 'src/services/DecisionTreeService/DecisionTreeService';
import ModuleProgressBar, { SlotProgress } from './ModuleProgressBar';
import { useAppSelector } from 'src/store/hooks';
import DOMPurify from 'dompurify';
import { ModuleDataDB, slotIsVisibleForUser } from '@app/common';
import { getSection, getGameState } from 'src/services/DBService/DbService';
import { isSlotCompleted } from 'src/utils/moduleCompletion';
import fallbackIcon from '../../../../static/icons/modules/emotion.png';

type Properties = {
    moduleData: ModuleDataDB;
    allModules: Array<ModuleDataDB>;
    moduleState?: ModuleStateDB;
    userStartDate: string | undefined;
    userProperties: string[]
    ref?: RefObject<HTMLDivElement>;
};

const ModuleOverviewItem = (props: Properties) => {
    const navigate = useNavigate();
    const { moduleData, moduleState, ref } = props;
    const config = useAppSelector(state => state.globalState.config);
    const [achievements, setAchievements] = React.useState<SlotProgress[]>([]);

    React.useEffect(() => {
        const calculateAchievements = async () => {
            const results: SlotProgress[] = [];
            const slotIds = Object.keys(moduleData.slots);
            
            for (let index = 0; index < Object.values(moduleData.slots).length; index++) {
                const slot = Object.values(moduleData.slots)[index];
                if (slotIsVisibleForUser(props.userProperties, slot, moduleState?.modifiers || [])) {
                    const slotId = slotIds[index] as `slot_${string}`;
                    const isSlotComplete = isSlotCompleted(
                        slotId,
                        slot,
                        moduleState,
                        props.userProperties,
                        config?.enableTransientSessionModes === true
                    );
                    
                    // count total pages and completed pages across all sessions in this slot
                    let totalPages = 0;
                    let completedPages = 0;
                    
                    // If usePerPageModuleProgressBarSlices is false or not set, only show one slice per slot
                    if (!config?.usePerPageModuleProgressBarSlices) {
                        totalPages = 1;
                        completedPages = isSlotComplete ? 1 : 0;
                    } else {
                        // Original per-page logic
                        const visibleSessionIds = getVisibleSessionIdsForUser(slot, moduleState?.modifiers || [], props.userProperties);
                        for (const sessionId of visibleSessionIds) {
                        const session = slot.sessions[sessionId];
                        if (session) {
                            // get visible sections for this session
                            const visibleSections = await getVisibleSections(
                                session, 
                                moduleState?.modifiers || [], 
                                moduleState?.elementValues || {},
                                props.userProperties
                            );
                            
                            // for each visible section, fetch it and count its items (pages)
                            for (const sectionRef of visibleSections) {
                                try {
                                    const sectionData = await getSection(sectionRef.id);
                                    const itemIds = Object.keys(sectionData.items);
                                    
                                    // check if each item (page) has been answered
                                    // only count pages with input elements or games (exclude display-only pages)
                                    const inputTypes = ['input_text', 'input_select', 'input_modifiers', 'input_range', 'input_check', 'input_number', 'input_duration', 'input_audio'];
                                    
                                    for (const itemId of itemIds) {
                                        const item = sectionData.items[itemId as `item_${string}`];
                                        if (item.type === 'page') {
                                            // check if page has any input elements
                                            const pageElements = Object.values(item.elements);
                                            const hasInputElements = pageElements.some(element => 
                                                inputTypes.includes(element.type)
                                            );
                                            
                                            if (hasInputElements) {
                                                // only count pages with input elements
                                                totalPages++;
                                                
                                                // get all input elements from the page
                                                const inputElements = pageElements.filter(element => 
                                                    inputTypes.includes(element.type)
                                                );
                                                
                                                // check if all input elements have values saved
                                                const pageKey = `${sectionRef.id}__${itemId}`;
                                                const allInputsAnswered = inputElements.every((element, idx) => {
                                                    // find the element ID by matching the element in the original items structure
                                                    const elementEntries = Object.entries(item.elements);
                                                    const matchingEntry = elementEntries.find(([_, el]) => el === element);
                                                    if (matchingEntry) {
                                                        const [elementId] = matchingEntry;
                                                        const fullKey = `${pageKey}__${elementId}`;
                                                        return moduleState?.elementValues?.[fullKey] !== undefined;
                                                    }
                                                    return false;
                                                });
                                                
                                                if (allInputsAnswered && inputElements.length > 0) {
                                                    completedPages++;
                                                }
                                            }
                                            // display-only pages are excluded from both total and completed counts
                                        } else if (item.type === 'game') {
                                            // games are always counted
                                            totalPages++;
                                            
                                            /**
                                             * Game completion: Currently marks as complete if any game state document exists in userdb.
                                             * Note: This doesn't verify actual game completion - just that the game was started.
                                             * For accurate completion tracking (if games are used by future tenants), implement:
                                             *     - Fetch game state from DB (already done below)
                                             *     - Check the game's "gametype" property from module data
                                             *     - Validate completion based on gametype-specific structure:
                                             *         Examples:
                                             *         - index_cards: verify all cards have been turned (state.turned array length matches cards count and all values are true)
                                             *         - gap_text: verify all items with type "term" have values filled in state
                                             *         - category_drop: verify all item indexes exist in state.doneItemIds
                                             *         - (similar checks for other gametypes)
                                             */
                                            // construct game state ID: game_<path>?<state_property>
                                            const gamePath = `/modules/${moduleData._id}/slots/${slotId}/${sessionId}/${sectionRef.id}/${itemId}`;
                                            const stateProperties = config?.enableTransientSessionModes && moduleState?.finished[`${slotId}__${sessionId}`]
                                                ? moduleState.finished[`${slotId}__${sessionId}`]
                                                : ['PREGNANCY', 'POSTPARTUM'];
                                            
                                            // check if game state exists for any of the state properties
                                            let gameCompleted = false;
                                            for (const stateProp of stateProperties) {
                                                const gameStateId = `game_${gamePath}?${stateProp}`;
                                                const gameState = await getGameState(gameStateId);
                                                if (gameState) {
                                                    gameCompleted = true;
                                                    break;
                                                }
                                            }
                                            
                                            if (gameCompleted) {
                                                completedPages++;
                                            }
                                        }
                                    }
                                } catch (error) {
                                    console.error(`Failed to fetch section ${sectionRef.id}:`, error);
                                }
                            }
                        }
                        
                        // Ensure at least 1 page subdivision for visual consistency
                        if (totalPages === 0) {
                            totalPages = 1;
                        }
                    }
                                            // Ensure at least 1 page subdivision for visual consistency
                        if (totalPages === 0) {
                            totalPages = 1;
                        }
                    }
                                        results.push({
                        isSlotComplete,
                        completedPages,
                        totalPages
                    });
                }
            }
            
            setAchievements(results);
        };
        
        calculateAchievements();
    }, [moduleData, moduleState, props.userProperties, config?.enableTransientSessionModes]);

    // Check visibility after all hooks are called
    if (!moduleIsVisibleForUser(props.moduleData, moduleState?.modifiers || [], props.allModules, props.userStartDate, props.userProperties)) {
        return <></>;
    }

    let iconClick = () => {
        if (
            moduleData.startSection !== undefined &&
            (!moduleState || !moduleState.finished.start)
        ) {
            navigate(`${moduleData._id}/start`);
        } else {
            navigate(`${moduleData._id}/slots`);
        }
    };

    const safeSvg: string = DOMPurify.sanitize(props.moduleData.icon || '');

    return (
        <div className="module-overview-module-item" onClick={iconClick} ref={ref}>
            {moduleData.icon ? (
                <div className="module-item-icon">
                    <div className="module-item-icon-image" dangerouslySetInnerHTML={{ __html: safeSvg }} />
                </div>
            ) : (
                <div className="module-item-fallback-icon">
                    <div style={{ backgroundColor: config?.themeColor }}>
                        <img className="module-item-icon-image" src={fallbackIcon} />
                    </div>
                </div>
            )}
            <div className="module-item-progressbar">
                <ModuleProgressBar achievements={achievements} />
            </div>
            <div className="module-item-title">
                {props.moduleData.name}
            </div>
        </div >
    );
};
export default ModuleOverviewItem;